import type {
  HekateBusFieldValue,
  HekateDisplayField,
  HekateDisplayForm,
} from '@onshape/www-inertia';
import { ErrorTagUtil, FieldHtmlNameUtil } from '@onshape/www-inertia';

import type { OnSubmitFormFunction } from '../../../../model/OnSubmitFormFunction';
import hekateBus from '../../../../bus';
import ExternalUtil from '../../../ExternalUtil';
import CheckboxFieldCoordinator from './CheckboxFieldCoordinator';
import FormAppCoordinator from './FormAppCoordinator';
import FormFacetCoordinator from './FormFacetCoordinator';
import FormSubmitter from './FormSubmitter';
import RecaptchaCoordinator from './RecaptchaCoordinator';
import SelectFieldCoordinator from './SelectFieldCoordinator';
import SubmitFieldCoordinator from './SubmitFieldCoordinator';
import TextInputFieldCoordinator from './TextInputFieldCoordinator';
import ErrorUtil from '../../../util/ErrorUtil';
import TrackingUtil from '../../../util/TrackingUtil';
import UnifiedSignupFormFacetCoordinator from './UnifiedSignup/UnifiedSignupFormFacetCoordinator';
import AutoCompleteFieldCoordinator from './AutoCompleteFieldCoordinator';
import PhoneNumberFieldCoordinator from './PhoneNumberFieldCoordinator';
import AgeVerificationFieldCoordinator from './AgeVerificationFieldCoordinator';
import MultiSelectFieldCoordinator from './MultiSelectFieldCoordinator';
import type { SetupContext } from 'vue';
import { wwwHekateFormDefinitions } from '../../../../../composables/useFormFiles';
import type { ButtonPortableText } from '~/sanity/types';

type ResponseLike = {
  status: number;
  json(): object;
};

class FormAppCoordinatorProvider {
  private formDefinition!: Ref<HekateDisplayForm | null>;

  private formElement!: Ref<HTMLFormElement | null>;

  private appElement!: Ref<HTMLFormElement | null>;

  private formSubmitter!: FormSubmitter;

  /** Field value to send over the bus */
  private busFieldValues!: Ref<HekateBusFieldValue[]>;

  /** True if this form shows recaptcha */
  private isRecaptcha!: Ref<boolean>;

  /** True if recaptcha is solved. */
  private isRecaptchaSolved!: Ref<boolean>;

  /** True if Recaptcha is V3 - default to true */
  private isRecaptchaV3!: Ref<boolean>;

  private isRecaptchaV2!: Ref<boolean>;

  private recaptchaSiteKey!: Ref<string>;

  private hasFailedRecaptchaV3!: Ref<boolean>;

  /** True if form is currently being submitted. */
  private isFormSubmitting!: Ref<boolean>;

  /** True if we should show the "Thank you for submitting the form" message. */
  private isShowThankYou!: Ref<boolean>;

  /** True if we should show the success message (instead of the thank you) */
  private isShowSuccessMessage!: Ref<boolean>;

  /** Text the user can C&P into their email for better support. */
  private supportEmailText!: Ref<string>;

  /** FormVariant define a field for BIZOPS */
  private formVariant!: string;

  /** Hidden fields for editors on CP */
  private hiddenStaticField1!: string;

  private hiddenStaticField2!: string;

  private submitText!: string;

  /** Html to show on success (innerHTML) */
  private successMessagePortableText!: Ref<ButtonPortableText | undefined>;

  /** Set to true if we get 409 response back from cad invite. */
  private isShow409Error!: Ref<boolean>;

  /** True if the form cannot loaded (e.g. network error) */
  private isFormUnloadable!: Ref<boolean>;

  /** True if the form should not be submitted */
  private isSubmitDisabled!: Ref<boolean>;

  /** show unvalid ref code Error */
  private formNotValidErrorMsg!: Ref<string>;

  /** show the form */
  private isShowForm!: Ref<boolean>;

  /** formId for this App */
  private formId!: string;

  /** Redirect url on success (if not empty) */
  private redirectUrl!: string;

  /** Google Analytics Conversion Goal Category */
  private gaCategory!: string;

  /** Google Analytics Conversion Goal Action */
  private gaAction!: string;

  /** The main app for the form an associated with formId */
  public formApp = () =>
    new FormAppCoordinator({
      formId: this.formId,
      formDefinition: this.formDefinition,
      busFieldValues: this.busFieldValues,
      appElement: this.appElement,
      onSubmitForm: this.onSubmitForm.bind(this),
      isFormUnloadable: this.isFormUnloadable,
      formVariant: this.formVariant,
      hiddenStaticField1: this.hiddenStaticField1,
      hiddenStaticField2: this.hiddenStaticField2,
      formNotValidErrorMsg: this.formNotValidErrorMsg,
    }).setup();

  /** Used for the entire form facet. */
  public formFacet = () =>
    new FormFacetCoordinator({
      onSubmitForm: this.onSubmitForm.bind(this),
      formElement: this.formElement,
      formDefinition: this.safeFormDefinition(),
      isFormSubmitting: this.isFormSubmitting,
      isShowThankYou: this.isShowThankYou,
      isShowSuccessMessage: this.isShowSuccessMessage,
      supportEmailText: this.supportEmailText,
      successMessagePortableText: this.successMessagePortableText,
      isShow409Error: this.isShow409Error,
      isShowForm: this.isShowForm,
    }).setup();

  /** Used for the entire form facet. */
  public unifiedSignupFormFacet = () =>
    new UnifiedSignupFormFacetCoordinator({
      onSubmitForm: this.onSubmitForm.bind(this),
      formElement: this.formElement,
      formDefinition: this.safeFormDefinition(),
      isFormSubmitting: this.isFormSubmitting,
      isShowThankYou: this.isShowThankYou,
      isShowSuccessMessage: this.isShowSuccessMessage,
      supportEmailText: this.supportEmailText,
      successMessagePortableText: this.successMessagePortableText,
      isShow409Error: this.isShow409Error,
      startScreen: this.hiddenStaticField1,
      submitText: this.submitText,
      buildSupportEmailErrorText: () =>
        this.formSubmitter.buildSupportEmailErrorText('signup-errors'),
    }).setup();

  /** Used for `<select>` form fields */
  public selectField = ({
    field,
    modelValue,
    context,
  }: {
    field: Ref<HekateDisplayField>;
    modelValue: Ref<string>;
    context: SetupContext;
  }) =>
    new SelectFieldCoordinator({
      context,
      field,
      modelValue,
      formDefinition: this.safeFormDefinition(),
    }).setup();

  /** Used for `<select>` form fields */
  public checkboxField = ({
    field,
    modelValue,
    context,
  }: {
    field: Ref<HekateDisplayField>;
    modelValue: Ref<boolean>;
    context: SetupContext;
  }) =>
    new CheckboxFieldCoordinator({
      context,
      field,
      modelValue,
      formDefinition: this.safeFormDefinition(),
    }).setup();

  /** Used for `<input>` form fields */
  public textInputField = ({
    field,
    modelValue,
    context,
  }: {
    field: Ref<HekateDisplayField>;
    modelValue: Ref<string>;
    context: SetupContext;
  }) =>
    new TextInputFieldCoordinator({
      context,
      field,
      modelValue,
      formDefinition: this.safeFormDefinition(),
    }).setup();

  public autoCompleteField = ({
    field,
    modelValue,
    context,
  }: {
    field: Ref<HekateDisplayField>;
    modelValue: Ref<string>;
    context: SetupContext;
  }) =>
    new AutoCompleteFieldCoordinator({
      context,
      field,
      modelValue,
      formDefinition: this.safeFormDefinition(),
    }).setup();

  public phoneNumberField = ({
    field,
    modelValue,
    context,
  }: {
    field: Ref<HekateDisplayField>;
    modelValue: Ref<string>;
    context: SetupContext;
  }) =>
    new PhoneNumberFieldCoordinator({
      context,
      field,
      modelValue,
      formDefinition: this.safeFormDefinition(),
    }).setup();

  public ageVerificationField = ({
    field,
    modelValue,
    context,
  }: {
    field: Ref<HekateDisplayField>;
    modelValue: Ref<string>;
    context: SetupContext;
  }) =>
    new AgeVerificationFieldCoordinator({
      context,
      field,
      modelValue,
      formDefinition: this.safeFormDefinition(),
    }).setup();

  public multiSelectField = ({
    field,
    modelValue,
    context,
  }: {
    field: Ref<HekateDisplayField>;
    modelValue: Ref<string>;
    context: SetupContext;
  }) =>
    new MultiSelectFieldCoordinator({
      context,
      field,
      modelValue,
      formDefinition: this.safeFormDefinition(),
    }).setup();

  /** Optional recaptcha. */
  public recaptchaField = ({ context }: { context: SetupContext }) =>
    new RecaptchaCoordinator({
      isRecaptcha: this.isRecaptcha,
      isRecaptchaSolved: this.isRecaptchaSolved,
      context,
      recaptchaSiteKey: this.recaptchaSiteKey,
      formDefinition: this.safeFormDefinition(),
      isRecaptchaV2: this.isRecaptchaV2,
      isRecaptchaV3: this.isRecaptchaV3,
      hasFailedRecaptchaV3: this.hasFailedRecaptchaV3,
    }).setup();

  /** The submit button. */
  public submitField = ({ field }: { field: Ref<HekateDisplayField> }) =>
    new SubmitFieldCoordinator({
      field,
      isRecaptchaSolved: this.isRecaptchaSolved,
      isSubmitDisabled: this.isSubmitDisabled,
      onSubmitForm: this.onSubmitForm.bind(this),
      submitText: this.submitText,
    }).setup();

  /** Return the form definition or throw if it is null. */
  private safeFormDefinition(): Ref<HekateDisplayForm> {
    if (this.formDefinition.value === null) {
      throw ErrorUtil.internalError({ msg: 'form definition is null' });
    }

    const formDefinition: Ref<HekateDisplayForm> = this.formDefinition as Ref<HekateDisplayForm>;
    return formDefinition;
  }

  private async loadFormTranslations() {
    if (window.wwwHekateTranslations) {
      return;
    }

    const translationsAsset = await useStaticAsset('/en/translations/forms.js', {
      tag: 'translations-forms.js',
    });
    if (!translationsAsset || !translationsAsset.cdnPathname) {
      return window.errorReporter?.postToSentry({
        tag: ErrorTagUtil.hekateLoad,
        msg: 'Could not find translations static asset with pathname "/en/translations/forms.js"',
      });
    }
    ExternalUtil.createScriptTag({
      src: translationsAsset.cdnPathname,
      isAsync: false,
      className: 'form-translations',
    });

    await ExternalUtil.fluidWait({
      timeoutMilliseconds: 5000,
      cb: async () => !!window.wwwHekateTranslations,
    });

    if (!window.wwwHekateTranslations) {
      window.errorReporter?.postToSentry({
        tag: ErrorTagUtil.hekateLoad,
        msg: 'Could not load translations',
      });
    }
  }

  /**
   * Load the form definition and query the
   * `hekateEventBus` for any values to inject
   */
  private async loadFormDefinition(formId: string) {
    console.log('Loading form', formId);
    if (!formId) {
      return;
    }

    const safeOrigin = ExternalUtil.getSafeOrigin();

    let response: ResponseLike;
    try {
      await RsvpUtil.fluentWait({
        predicate: () => !!wwwHekateFormDefinitions[formId],
        timeout: 5 * 1000,
        minWait: 0,
      });

      // this may be loaded by script tags at the very top of the page, loaded in crownpeak
      const def = wwwHekateFormDefinitions[formId];
      if (def) {
        response = {
          status: 200,
          json: () => def,
        };
      } else {
        throw new Error(`Could not load form with formId: ${formId}`);
      }
    } catch (e) {
      this.isFormUnloadable.value = true;

      const msg = `Unable to load Form Definition formId: ${formId} - ${safeOrigin}`;

      ErrorUtil.safePostMessageToSentry({
        tag: ErrorTagUtil.hekateLoad,
        msg,
        error: e,
      });

      console.error(`${ErrorTagUtil.hekateLoad} - ${msg}`, e);

      TrackingUtil.mouseFlowPush(['setVariable', 'hekate-load-fail', 'misc-error']);

      this.removeFormLoader();

      return;
    }

    if (response.status !== 200) {
      this.isFormUnloadable.value = true;
      ErrorUtil.safePostMessageToSentry({
        tag: ErrorTagUtil.hekateLoad,
        msg: 'non-200',
      });

      this.removeFormLoader();

      const msg = `Could not load formId ${formId} - (${response.status})`;

      ErrorUtil.safePostMessageToSentry({
        tag: ErrorTagUtil.hekateLoad,
        msg,
      });

      TrackingUtil.mouseFlowPush(['setVariable', 'hekate-load-fail', 'non-200']);
      return;
    }

    const [form] = await Promise.all([response.json(), this.loadFormTranslations()]);

    this.removeFormLoader();

    hekateBus.dispatchEvent('form-definition', form);

    hekateBus.dispatchEvent('wa-push', {
      event: 'FormLoaded',
      data: {
        formId,
      },
    });

    setTimeout(() => {
      hekateBus.dispatchEvent('request-form-injections', {});
    }, 500);

    // XXX: This is very aggressive and should be scaled back eventually
    let maxTries = 10;
    const interval = setInterval(() => {
      hekateBus.dispatchEvent('request-form-injections', {});
      if (maxTries < 1) {
        clearInterval(interval);
      }
      maxTries -= 1;
    }, 2000);
  }

  /** Submit the form */
  private onSubmitForm: OnSubmitFormFunction = async (evt: Event) =>
    this.formSubmitter.onSubmitForm({ evt });

  private removeFormLoader() {
    const container = document.querySelectorAll(`div.formfort-loading[form-id="${this.formId}"]`);
    if (!container.entries) {
      return;
    }
    container.forEach((el) => el.classList.remove('formfort-loading'));
  }

  public setup({
    formId,
    redirectUrl,
    gaCategory,
    submitText,
    gaAction,
    successMessagePortableText,
    formVariant,
    hiddenStaticField1,
    hiddenStaticField2,
  }: {
    formId: string;
    redirectUrl: string;
    gaCategory: string;
    submitText: string;
    gaAction: string;
    successMessagePortableText?: ButtonPortableText;
    formVariant: string;
    hiddenStaticField1: string;
    hiddenStaticField2: string;
  }) {
    this.isFormUnloadable = ref(false);
    this.formId = formId;
    this.loadFormDefinition(formId); // todo remove param

    this.redirectUrl = redirectUrl;
    this.gaCategory = gaCategory;
    this.submitText = submitText;
    this.gaAction = gaAction;
    this.successMessagePortableText =
      successMessagePortableText ? ref(successMessagePortableText) : ref(undefined);
    this.formVariant = formVariant;
    this.hiddenStaticField1 = hiddenStaticField1;
    this.hiddenStaticField2 = hiddenStaticField2;
    this.isRecaptchaSolved = ref(false);
    this.isRecaptchaV3 = ref(true);
    this.isRecaptchaV2 = ref(false);
    this.recaptchaSiteKey = ref('');
    this.hasFailedRecaptchaV3 = ref(false);
    this.isFormSubmitting = ref(false);
    this.isShowThankYou = ref(false);
    this.isShowSuccessMessage = ref(false);
    this.formElement = ref(null);
    this.appElement = ref(null);
    this.formDefinition = ref(null);
    this.busFieldValues = ref([]);
    this.supportEmailText = ref('');
    this.isShow409Error = ref(false);
    this.formNotValidErrorMsg = ref('');

    this.isShowForm = computed(
      () =>
        !(this.isShow409Error.value || this.isShowThankYou.value || this.isShowSuccessMessage.value)
    );

    this.isRecaptcha = computed(() => {
      if (!this.formDefinition.value) {
        return false;
      }
      return this.formDefinition.value.isRecaptcha;
    });

    this.isSubmitDisabled = computed(() => {
      if (this.isFormSubmitting.value) {
        return true;
      }
      if (!this.isRecaptcha.value) {
        return false;
      }
      if (this.isRecaptchaV2.value && !this.isRecaptchaSolved.value) {
        return true;
      }
      const recaptchaField = ExternalUtil.findDisplayFieldValOrThrow({
        formDefinition: this.formDefinition as Ref<HekateDisplayForm>,
        fieldName: FieldHtmlNameUtil.recaptcha,
      });
      if (this.isRecaptchaV2.value && !recaptchaField.value) {
        return true;
      }
      return false;
    });

    this.formSubmitter = new FormSubmitter({
      // maybe use safeFormDefinition()?
      formDefinition: this.formDefinition,
      formElement: this.formElement,
      isSubmitDisabled: this.isSubmitDisabled,
      isFormSubmitting: this.isFormSubmitting,
      recaptchaSiteKey: this.recaptchaSiteKey,
      hasFailedRecaptchaV3: this.hasFailedRecaptchaV3,
      supportEmailText: this.supportEmailText,
      appElement: this.appElement,
      isShowThankYou: this.isShowThankYou,
      isShowSuccessMessage: this.isShowSuccessMessage,
      isShow409Error: this.isShow409Error,
      redirectUrl: this.redirectUrl,
      gaCategory: this.gaCategory,
      gaAction: this.gaAction,
      successMessagePortableText: this.successMessagePortableText,
    });
  }
}

export default FormAppCoordinatorProvider;
